import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { roleHistory } from '../data/experience';
import { ExperienceGraph } from './ExperienceGraph';
import { SectionLabel } from './SectionLabel';
const skills = ['Visual Design', 'UX Design', 'Prototyping', 'UX Research', 'User Testing', 'Interaction / Motion', 'Front-end', 'UX Writing'];
const tools = ['Figma', 'Sketch', 'Framer', 'Principle', 'Notion', 'Linear', 'Webflow'];
const education = [{
  degree: 'BFA, Graphic Design',
  school: 'University of South Florida',
  year: '2007 — 2011'
}];
const reveal = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
export function About() {
  const reduceMotion = useReducedMotion();
  const [photoFailed, setPhotoFailed] = useState(false);
  return <section id="about" className="site-container py-24 md:py-32" data-id="element-15" data-mp="3mj2gantf44p9">
      <div className="border-t border-border pt-5" data-id="element-16" data-mp="3rgaxom6qs4pg">
        <SectionLabel data-id="element-17" data-mp="3req1yt8oyfq0">About</SectionLabel>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-14 lg:mt-14 lg:grid-cols-12 lg:gap-10" data-id="element-18" data-mp="2lrh6jojlw360">
        <aside className="lg:col-span-4" data-id="element-19" data-mp="2grmhlqje5nnl">
          <div className="lg:sticky lg:top-28" data-id="element-20" data-mp="2gqt6yehbae06">
            <h2 className="max-w-sm text-balance text-4xl font-medium leading-[1.03] tracking-[-0.035em] md:text-5xl" data-id="element-21" data-mp="2cq0wxj0m852u">
              Thoughtful design starts with better questions.
            </h2>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-muted" data-id="element-22" data-mp="34i8dykk19jhr">
              I believe great design solves real problems elegantly—without
              drawing unnecessary attention to itself.
            </p>
            <div className="mt-10 aspect-square w-full overflow-hidden bg-border" data-id="element-23" data-mp="2vod8tciwvt24">
              {photoFailed ? <div className="flex h-full w-full items-center justify-center font-display text-4xl font-medium uppercase tracking-[0.08em] text-muted" role="img" aria-label="Alexander Kusminov" data-id="element-24" data-mp="2qpsic4tgonqv">
                  AK
                </div> : <img src="https://cdn.magicpatterns.com/uploads/g2ouv8uBuAvsReMFYbVXsw/image.png" alt="Alexander Kusminov" onError={() => setPhotoFailed(true)} className="aspect-square w-full object-cover grayscale" data-id="element-25" data-mp="686gawosysuk" />}
            </div>
          </div>
        </aside>

        <div className="space-y-20 lg:col-span-7 lg:col-start-6" data-id="element-26" data-mp="3jj5hy84ppeyn">
          <motion.section initial={reduceMotion ? false : 'hidden'} whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} variants={reveal} data-id="element-27" data-mp="3ok38p93sfhbr">
            <h3 className="font-display text-base font-medium uppercase tracking-[0.1em] text-foreground" data-id="element-28" data-mp="18symoag0bcxh">
              Background
            </h3>
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-muted" data-id="element-29" data-mp="1ctqwp5wpdlut">
              <p data-id="element-30" data-mp="2xdcpheybrdvj">
                With over a decade of experience in the design industry, I've
                had the privilege of working with startups and enterprise
                companies alike, helping them translate complex business
                requirements into intuitive, user-centric digital products.
              </p>
              <p data-id="element-31" data-mp="329bbivw6kjss">
                My approach is deeply rooted in minimalism and functionalism. I
                strip away the superfluous to uncover the core value of a
                product, ensuring every pixel serves a purpose. Whether it's a
                dense data dashboard or a consumer mobile app, my goal is to
                create clarity out of chaos.
              </p>
            </div>
          </motion.section>

          <motion.section initial={reduceMotion ? false : 'hidden'} whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} variants={reveal} data-id="element-32" data-mp="1vmfrcmuu4j1f">
            <h3 className="font-display text-base font-medium uppercase tracking-[0.1em] text-foreground" data-id="element-33" data-mp="1x44mre4lcvw0">
              Experience by industry
            </h3>
            <div className="mt-8" data-id="element-34" data-mp="223r1js02ik6x">
              <ExperienceGraph data-id="element-35" data-mp="272anvtikpxzt" />
            </div>
          </motion.section>

          <motion.section initial={reduceMotion ? false : 'hidden'} whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} variants={reveal} data-id="element-36" data-mp="27080zicpwkzb">
            <h3 className="font-display text-base font-medium uppercase tracking-[0.1em] text-foreground" data-id="element-37" data-mp="1jb5cbqjo1rvb">
              Experience
            </h3>
            <div className="mt-8 space-y-12" data-id="element-38" data-mp="qoc46n3y5qba">
              {roleHistory.map(company => <div key={company.company} data-id="element-39" data-mp="vmvu0ylnvm8x">
                  <div className="mb-5 flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between md:gap-6" data-id="element-40" data-mp="vluful9cmjmz">
                    <h4 className="break-words text-2xl font-medium leading-tight tracking-[-0.02em]" data-id="element-41" data-mp="o2uwgflw0s3m">
                      {company.company}
                    </h4>
                    <span className="shrink-0 font-display text-sm font-medium uppercase tracking-[0.08em] text-muted" data-id="element-42" data-mp="e4f98npze2ze">
                      ~{company.years} {company.years === 1 ? 'yr' : 'yrs'}
                    </span>
                  </div>
                  <ul className="space-y-3 border-l border-border pl-4 md:pl-5" data-id="element-43" data-mp="56lehh7v7hec">
                    {company.roles.map(role => <li key={role.title} className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:justify-between md:gap-6" data-id="element-44" data-mp="3rjjvzhn1z1js">
                        <span className="break-words text-base text-foreground" data-id="element-45" data-mp="3hmfalk117a41">{role.title}</span>
                        <span className="shrink-0 text-sm text-muted" data-id="element-46" data-mp="3dkbwenmz9rf2">{role.range}</span>
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </motion.section>

          <motion.section initial={reduceMotion ? false : 'hidden'} whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} variants={reveal} data-id="element-47" data-mp="37wgofxr79efd">
            <h3 className="font-display text-base font-medium uppercase tracking-[0.1em] text-foreground" data-id="element-48" data-mp="3mnze6n9v2nbx">
              Education
            </h3>
            <div className="mt-8" data-id="element-49" data-mp="3cxw9hptdnkxr">
              {education.map(item => <div key={item.degree} className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between md:gap-6" data-id="element-50" data-mp="3cvtltcfp63w7">
                  <div data-id="element-51" data-mp="9ge3zp12uq0i">
                    <h4 className="text-xl font-medium" data-id="element-52" data-mp="9fw7crai69s2">{item.degree}</h4>
                    <p className="mt-1 text-sm text-muted" data-id="element-53" data-mp="e85wi53sn5ta">{item.school}</p>
                  </div>
                  <span className="shrink-0 font-display text-sm font-medium uppercase tracking-[0.08em] text-muted" data-id="element-54" data-mp="nlxw6uxebl0s">
                    {item.year}
                  </span>
                </div>)}
            </div>
          </motion.section>

          <motion.section initial={reduceMotion ? false : 'hidden'} whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} variants={reveal} className="grid gap-12 md:grid-cols-2" data-id="element-55" data-mp="xedfbvkfifn9">
            <div data-id="element-56" data-mp="elhxrzvif42s">
              <h3 className="font-display text-base font-medium uppercase tracking-[0.1em] text-foreground" data-id="element-57" data-mp="em1drb1lnie6">Capabilities</h3>
              <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3 text-sm text-muted" data-id="element-58" data-mp="9l3m87up9c00">
                {skills.map(skill => <li key={skill} data-id="element-59" data-mp="1pz3kledwctl5">{skill}</li>)}
              </ul>
            </div>
            <div data-id="element-60" data-mp="1l122avoaoyaq">
              <h3 className="font-display text-base font-medium uppercase tracking-[0.1em] text-foreground" data-id="element-61" data-mp="1l1llefpolsqc">Tools</h3>
              <ul className="mt-6 flex flex-wrap gap-x-4 gap-y-3 text-sm text-muted" data-id="element-62" data-mp="3fg8wb0n5vmk5">
                {tools.map(tool => <li key={tool} data-id="element-63" data-mp="3oiqhrcuzsb63">{tool}</li>)}
              </ul>
            </div>
          </motion.section>
        </div>
      </div>
    </section>;
}